import {
  NotificationsQuery,
  useAddUserToShippingLocationGroupsMutation,
  useAutocompleteShippingLocationGroupsQuery,
  useShippingLocationGroupsQuery,
  useUnsubscribeFromShippingLocationGroupsMutation,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useDebounce from '@/gf/hooks/useDebounce'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandSeparator,
} from '@/gf/components/ui/command'
import RedAlert from '@/gf/components/RedAlert'
import Spinner from '@/gf/components/Spinner'
import Action from '@/gf/components/Action'
import { ChevronsUpDown } from 'lucide-react'
import Checkbox from '@/gf/components/next/forms/Checkbox'
import { useEffect, useMemo, useState } from 'react'

type User = NonNullable<NotificationsQuery['user']>
type Org = NonNullable<NotificationsQuery['org']>

const LocationGroups = ({
  user,
  org,
  onChange,
}: {
  user: User
  org: Org
  onChange: () => void
}) => {
  const client = useGqlClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null)

  const [subscribeToLocationGroups] = useAddUserToShippingLocationGroupsMutation({ client })
  const [unsubscribeFromLocationGroups] = useUnsubscribeFromShippingLocationGroupsMutation({
    client,
  })

  // Get IDs of subscribed location groups
  const locationGroupIds = user.shippingLocationGroupNotifications.map(
    (n) => n.shippingLocationGroupId
  )

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useAutocompleteShippingLocationGroupsQuery({
    client,
    variables: {
      orgId: org.id,
      filter: JSON.stringify(['search', search]),
    },
  })

  const { data: selectedLocationGroupsData, error: selectedLocationGroupsError } =
    useShippingLocationGroupsQuery({
      client,
      variables: {
        orgId: org.id,
        filter: JSON.stringify(['id_in', locationGroupIds]),
      },
    })

  const [selectedLocationGroups, setSelectedLocationGroups] = useState(
    selectedLocationGroupsData?.org?.shippingLocationGroups ?? []
  )
  const selectedLocationGroupIds = useMemo(
    () => selectedLocationGroups.map((s) => s.id),
    [selectedLocationGroups]
  )

  const searchLocationGroups = useMemo(() => {
    return (
      searchData?.org?.shippingLocationGroups.filter(
        (l) => !selectedLocationGroupIds.includes(l.id)
      ) ?? []
    )
  }, [searchData, selectedLocationGroupIds])

  // Only update the selected group after the popover is closed
  useEffect(() => {
    if (!open) {
      setSelectedLocationGroups(selectedLocationGroupsData?.org?.shippingLocationGroups ?? [])
    }
  }, [open, selectedLocationGroupsData])

  const handleToggle = (locationGroupId: string) => {
    const isCurrentlySubscribed = locationGroupIds.includes(locationGroupId)
    setLoadingGroupId(locationGroupId)

    const mutation = isCurrentlySubscribed
      ? unsubscribeFromLocationGroups({
          variables: { userId: user.id, locationGroupIds: [locationGroupId] },
        })
      : subscribeToLocationGroups({
          variables: { userId: user.id, locationGroupIds: [locationGroupId] },
        })

    mutation
      .then(() => {
        onChange()
      })
      .finally(() => {
        setLoadingGroupId(null)
      })
  }

  const LocationGroupCheckboxItem = ({
    locationGroup,
  }: {
    locationGroup: { id: string; name: string }
  }) => {
    const selected = locationGroupIds.some((s) => s === locationGroup.id)
    const isLoading = loadingGroupId === locationGroup.id
    const onSelectionChange = () => handleToggle(locationGroup.id)

    return (
      <CommandItem
        key={locationGroup.id}
        className="group"
        value={locationGroup.id}
        onSelect={() => {
          setSearch('')
          setOpen(false)
          onSelectionChange()
        }}
      >
        {isLoading ? (
          <Spinner className="mx-0" size="xs" color="blue" />
        ) : (
          <Checkbox
            className={!selected ? 'group-hover:visible invisible' : undefined}
            checked={selected}
            color="blue"
            onChange={() => onSelectionChange()}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {locationGroup.name}
      </CommandItem>
    )
  }

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold">My Location Groups</div>
      <div className="prose">Select the location groups you want to be notified for.</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>
            <Action.S className="flex items-center gap-x-1" size="sm">
              <span className="truncate max-w-64">
                {locationGroupIds.length === 0
                  ? 'Select location groups...'
                  : locationGroupIds.length === 1
                    ? (selectedLocationGroupsData?.org?.shippingLocationGroups.find(
                        (l) => l.id === locationGroupIds[0]
                      )?.name ?? 'Loading...')
                    : `${locationGroupIds.length} Location Groups`}
              </span>
              <ChevronsUpDown className="-mr-1 w-4 h-4 inline-block" />
            </Action.S>
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Select location group..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {searchError || selectedLocationGroupsError ? (
                <RedAlert className="bg-transparent" title="Error searching location groups." />
              ) : searchLoading ? (
                <CommandLoading>
                  <div className="flex flex-row gap-x-2 items-center justify-center">
                    <Spinner className="mx-0" size="xs" color="blue" />
                    Searching location groups...
                  </div>
                </CommandLoading>
              ) : !debouncedSearch ? (
                <CommandEmpty>Search for location group.</CommandEmpty>
              ) : (
                <CommandEmpty>No location groups found.</CommandEmpty>
              )}
              {selectedLocationGroups.length > 0 && (
                <>
                  <CommandGroup>
                    {selectedLocationGroups.map((locationGroup) => (
                      <LocationGroupCheckboxItem
                        key={locationGroup.id}
                        locationGroup={locationGroup}
                      />
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              <CommandGroup>
                {searchLocationGroups.map((locationGroup) => (
                  <LocationGroupCheckboxItem key={locationGroup.id} locationGroup={locationGroup} />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default LocationGroups
